import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, Sparkles, Mail, Lock, Loader2, ShieldCheck, EyeOff, Gift } from "lucide-react";
import { toast } from "sonner";
import { track } from "../lib/analytics";
import PrivacyAssuranceLink from "../components/PrivacyAssuranceLink";

function formatErr(detail) {
  if (detail == null) return "Something went wrong. Try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function StartTrialPage() {
  const navigate = useNavigate();
  const { setUser, setOnboarding, checkAuth } = useAuth();
  const [mode, setMode] = useState("signup"); // signup | login
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [redeemCode, setRedeemCode] = useState("");
  const [showRedeem, setShowRedeem] = useState(false);
  const [loading, setLoading] = useState(false);

  // If PricingPage stored a pending code (logged-out user tried to redeem),
  // pre-fill it here so signup auto-applies it.
  React.useEffect(() => {
    const pending = sessionStorage.getItem("ascend_pending_code");
    if (pending) {
      setRedeemCode(pending);
      setShowRedeem(true);
      sessionStorage.removeItem("ascend_pending_code");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const em = email.trim().toLowerCase();
    if (!em || !em.includes("@")) return toast.error("Valid email please.");
    if (!password || password.length < 8) return toast.error("Password needs to be at least 8 characters.");
    setLoading(true);
    try {
      const endpoint = mode === "signup" ? "/auth/email/signup" : "/auth/email/login";
      const payload = mode === "signup"
        ? { email: em, password, name, redeem_code: redeemCode.trim() || undefined }
        : { email: em, password };
      const r = await axios.post(`${API}${endpoint}`, payload);
      localStorage.setItem("ascend_session_token", r.data.session_token);
      axios.defaults.headers.common.Authorization = `Bearer ${r.data.session_token}`;
      // Hydrate AuthContext IMMEDIATELY so ProtectedRoute doesn't bounce us back
      // to /start. The signup endpoint already returns the user object — just
      // set it directly. Onboarding will be empty for new signups (server returns
      // null for onboarding on the signup response, so we fetch it via checkAuth).
      if (r.data.user) {
        setUser(r.data.user);
        setOnboarding(r.data.onboarding || null);
      }
      // Backfill the rest (onboarding, etc.) but don't await — context already has user
      checkAuth().catch(() => {});
      track(mode === "signup" ? "email_signup_completed" : "email_login_completed");
      if (r.data.linked_existing_google_account) {
        toast.success("Welcome back! Password added to your existing account.", { duration: 5500 });
      } else {
        toast.success(mode === "signup" ? "Welcome!" : "Welcome back!");
      }
      // Surface redeem code result (success or failure)
      if (mode === "signup" && r.data.redeem) {
        if (r.data.redeem.ok) {
          toast.success(r.data.redeem.message || "Code redeemed!", { duration: 6000 });
        } else if (r.data.redeem.error) {
          toast.error(`Code didn't apply: ${r.data.redeem.error}`, { duration: 6000 });
        }
      }
      // Decide destination based on what the user has already completed.
      // The /api/auth/email/{signup,login} response includes `onboarding` for
      // returning users — if it's complete they go straight to the dashboard;
      // partially-onboarded users resume at /welcome; brand-new accounts pick
      // a mentor first.
      const ob = r.data.onboarding;
      let dest = "/mentor";
      if (ob?.completed) dest = "/dashboard";
      else if (ob?.mentor_id) dest = "/welcome";
      setTimeout(() => navigate(dest), 250);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // If user tried to log in with a Google-only email, auto-switch to signup
      // mode so they can create a password that gets linked to their existing
      // Google account (handled server-side).
      const detailStr = typeof detail === "string" ? detail : "";
      if (
        mode === "login" &&
        (detailStr.includes("Google sign-in") || detailStr.toLowerCase().includes("created with google"))
      ) {
        setMode("signup");
        toast.message(
          "Set a password for your existing account",
          {
            description:
              "This email was created with Google sign-in. Enter a new password — we'll link it to your existing account.",
            duration: 8000,
          }
        );
      } else {
        toast.error(formatErr(detail) || "Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f3ea] flex flex-col">
      <header className="px-6 py-6 max-w-7xl mx-auto w-full">
        <Link to="/" className="inline-flex items-center gap-2 font-body text-[14px] text-[#2b3442] hover:text-[#486a8f]">
          <ArrowLeft size={16} /> Back
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 pb-20">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#e6dcc6] mb-6">
              <Sparkles size={14} className="text-[#486a8f]" />
              <span className="font-body text-[12px] text-[#2b3442]">Free 3-Day Trial · Card required at Stripe</span>
            </div>
            <h1 className="font-display text-[40px] md:text-[48px] leading-[1.05] text-[#0f1724] tracking-tight mb-4">
              {mode === "login" ? (
                <>Welcome <span className="italic font-light text-[#486a8f]">back</span>.</>
              ) : (
                <>Meet your <span className="italic font-light text-[#486a8f]">AI mentor</span>.</>
              )}
            </h1>
          </div>

          <div className="rounded-2xl bg-white border border-[#eadfc9] p-7 shadow-[0_20px_60px_-30px_rgba(15,23,36,0.2)]">
            {/* Privacy Promise — visible BEFORE the form to set the tone */}
            <div data-testid="auth-privacy-promise" className="rounded-xl bg-[#f7f3ea] border border-[#eadfc9] p-4 mb-5">
              <div className="flex items-start gap-3">
                <ShieldCheck size={18} className="text-[#3e6b52] shrink-0 mt-0.5" />
                <div>
                  <p className="font-mono-eyebrow text-[10px] text-[#3e6b52] mb-1">YOUR PRIVACY PROMISE</p>
                  <p className="font-body text-[12.5px] text-[#2b3442] leading-relaxed">
                    Your conversations stay <span className="font-semibold text-[#0f1724]">inside Ascend</span>. We never sell your data, never share it for marketing, and never train public AI models on your private content. Treat us like a vault — only you can open it.{" "}
                    <Link to="/privacy" className="text-[#486a8f] underline">Read the full promise</Link>.
                  </p>
                </div>
              </div>
            </div>

            {/* Email/Password — PRIMARY */}
            <form onSubmit={submit} className="space-y-3">
              {mode === "signup" && (
                <div className="relative">
                  <Input
                    data-testid="auth-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="First name (optional)"
                    className="h-11 pl-10"
                  />
                  <Sparkles size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9aa3ae]" />
                </div>
              )}
              <div className="relative">
                <Input
                  data-testid="auth-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  className="h-11 pl-10"
                />
                <Mail size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9aa3ae]" />
              </div>
              <div className="relative">
                <Input
                  data-testid="auth-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === "signup" ? "Password (min 8 chars)" : "Password"}
                  className="h-11 pl-10"
                />
                <Lock size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9aa3ae]" />
              </div>
              <Button
                data-testid="auth-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white font-medium text-[14px]"
              >
                {loading ? (
                  <><Loader2 size={14} className="mr-2 animate-spin" /> Please wait…</>
                ) : mode === "signup" ? (
                  <>Create account & start trial</>
                ) : (
                  <>Log in</>
                )}
              </Button>
              {mode === "signup" && (
                <p
                  data-testid="auth-trust-line"
                  className="text-center font-body text-[12px] text-[#6b7280] mt-3 leading-relaxed"
                >
                  No card. No autobill. Cancel anytime.
                </p>
              )}
            </form>

            {mode === "login" && (
              <div className="mt-3 text-center">
                <Link to="/forgot-password" data-testid="auth-forgot-password-link" className="font-body text-[12.5px] text-[#486a8f] hover:underline">
                  Forgot your password?
                </Link>
              </div>
            )}

            {mode === "signup" && (
              <div className="mt-4" data-testid="auth-redeem-section">
                {!showRedeem ? (
                  <button
                    type="button"
                    data-testid="auth-redeem-toggle"
                    onClick={() => setShowRedeem(true)}
                    className="inline-flex items-center gap-2 font-body text-[13px] text-[#486a8f] hover:underline"
                  >
                    <Gift size={14} /> Have a code? Redeem it
                  </button>
                ) : (
                  <div className="rounded-xl bg-[#f7f3ea] border border-[#eadfc9] p-3">
                    <label className="font-mono-eyebrow text-[10px] text-[#486a8f] block mb-2">
                      REDEEM CODE
                    </label>
                    <div className="relative">
                      <Input
                        data-testid="auth-redeem-input"
                        value={redeemCode}
                        onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                        placeholder="ENTER YOUR CODE"
                        className="h-10 pl-9 uppercase tracking-wide"
                      />
                      <Gift size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#486a8f]" />
                    </div>
                    <p className="font-body text-[11px] text-[#6b7280] mt-2 leading-relaxed">
                      Your code will apply after you create your account. No card needed if your code includes access.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="text-center mt-4">
              {mode === "login" ? (
                <button
                  data-testid="auth-toggle-signup"
                  onClick={() => setMode("signup")}
                  className="font-body text-[13px] text-[#486a8f] hover:underline"
                >
                  New to Ascend? Create an account
                </button>
              ) : (
                <button
                  data-testid="auth-toggle-login"
                  onClick={() => setMode("login")}
                  className="font-body text-[13px] text-[#486a8f] hover:underline"
                >
                  Already have an account? Log in
                </button>
              )}
            </div>

            <p className="font-body text-[11px] text-[#6b7280] text-center mt-5 leading-relaxed">
              No charge today. Plans from $8.88/month after trial. Cancel anytime.
            </p>
          </div>

          {/* Privacy commitments — shown below the form to reinforce trust */}
          <div data-testid="auth-trust-commitments" className="mt-6 grid grid-cols-3 gap-2">
            <div className="rounded-xl bg-white border border-[#eadfc9] p-3 text-center">
              <ShieldCheck size={16} className="text-[#3e6b52] mx-auto mb-1.5" />
              <p className="font-body text-[11px] text-[#2b3442] leading-tight font-medium">Encrypted in transit</p>
            </div>
            <div className="rounded-xl bg-white border border-[#eadfc9] p-3 text-center">
              <EyeOff size={16} className="text-[#3e6b52] mx-auto mb-1.5" />
              <p className="font-body text-[11px] text-[#2b3442] leading-tight font-medium">Never sold or shared</p>
            </div>
            <div className="rounded-xl bg-white border border-[#eadfc9] p-3 text-center">
              <Lock size={16} className="text-[#3e6b52] mx-auto mb-1.5" />
              <p className="font-body text-[11px] text-[#2b3442] leading-tight font-medium">Delete anytime</p>
            </div>
          </div>

          <p className="font-body text-[12px] text-[#6b7280] text-center mt-6">
            By continuing you agree to our{" "}
            <Link to="/terms" className="text-[#486a8f] hover:underline">Terms</Link> and{" "}
            <Link to="/privacy" className="text-[#486a8f] hover:underline">Privacy</Link>.
          </p>
          <div className="text-center mt-3">
            <PrivacyAssuranceLink />
          </div>
        </div>
      </main>
    </div>
  );
}
